import {
  ApolloLink,
  type FetchResult,
  type NextLink,
  Observable,
  type Operation,
} from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { Kind, OperationTypeNode } from "graphql";
import type { Subscription } from "zen-observable-ts";

const MAX_GRAPHQL_RETRIES = 3;

// Helper to decide if we should retry this result
function getGraphQLErrorExceptionName(err: {
  extensions?: unknown;
}): string | undefined {
  const { extensions } = err;
  if (!extensions || typeof extensions !== "object") {
    return undefined;
  }
  const maybeException = (extensions as Record<string, unknown>).exception;
  if (!maybeException || typeof maybeException !== "object") {
    return undefined;
  }
  const nameVal = (maybeException as Record<string, unknown>).name;
  return typeof nameVal === "string" ? nameVal : undefined;
}

function shouldRetryGraphQLResult(result: FetchResult): boolean {
  const errors = result.errors;
  if (!errors || errors.length === 0) {
    return false;
  }

  return errors.some(err => {
    const code = err.extensions?.code;
    const message = err.message;
    const extExceptionName = getGraphQLErrorExceptionName(err);
    const lowerMessage = (message ?? "").toLowerCase();

    // Detect TransactionSerializationError by name, code, or common message text
    const isTransactionSerializationError =
      lowerMessage.includes("transactionserializationerror") ||
      lowerMessage.includes("could not serialize access") ||
      code === "TRANSACTION_SERIALIZATION_ERROR" ||
      code === "TRANSACTION_SERIALIZATION_FAILURE" ||
      extExceptionName === "TransactionSerializationError";

    // Transient downstream failures: the gateway wraps upstream 5xx as a GraphQL
    // error with code DOWNSTREAM_SERVICE_ERROR. Retrying covers brief outages
    // without surfacing an error to the user.
    const isDownstreamServiceError = code === "DOWNSTREAM_SERVICE_ERROR";

    return isTransactionSerializationError || isDownstreamServiceError;
  });
}

// Optional: simple backoff
function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export const graphqlRetryLink = new ApolloLink(
  (operation: Operation, forward: NextLink) => {
    if (!forward) {
      // Shouldn't happen in practice, but be safe.
      return new Observable<FetchResult>(observer => {
        observer.complete();
      });
    }

    // Retry logic completes after one result — skip for subscriptions.
    const definition = getMainDefinition(operation.query);
    if (
      definition.kind === Kind.OPERATION_DEFINITION &&
      definition.operation === OperationTypeNode.SUBSCRIPTION
    ) {
      return forward(operation);
    }

    return new Observable<FetchResult>(observer => {
      let attempt = 0;
      let innerSub: Subscription | null = null;
      let isDone = false;

      const finishWithResult = (result: FetchResult) => {
        if (isDone) {
          return;
        }
        isDone = true;
        // Always surface GraphQL results (including errors) via next + complete.
        // Apollo expects GraphQL errors on the result, not as thrown errors.
        observer.next(result);
        observer.complete();
        innerSub?.unsubscribe();
      };

      const finishWithError = (err: unknown) => {
        if (isDone) {
          return;
        }
        isDone = true;
        observer.error(err);
        innerSub?.unsubscribe();
      };

      const runAttempt = () => {
        innerSub = forward(operation).subscribe({
          next: result => {
            const isRetryable = shouldRetryGraphQLResult(result);

            // Case 1: not retryable -> surface and complete
            if (!isRetryable) {
              finishWithResult(result);
              return;
            }

            // Case 2: we've already hit the max -> surface and complete
            if (attempt + 1 > MAX_GRAPHQL_RETRIES) {
              finishWithResult(result);
              return;
            }

            // Case 3: retry
            attempt += 1;
            const delayMs = 200 * 2 ** (attempt - 1); // 200, 400, 800...

            delay(delayMs)
              .then(() => {
                if (!isDone) {
                  innerSub?.unsubscribe();
                  runAttempt();
                }
              })
              .catch(() => {
                // If delay fails for some reason, just surface current result
                finishWithResult(result);
              });
          },

          error: err => {
            // Network / link errors after RetryLink: just bubble up
            finishWithError(err);
          },
        });
      };

      // Kick off first attempt
      runAttempt();

      // Teardown
      return () => {
        isDone = true;
        innerSub?.unsubscribe();
      };
    });
  },
);
